import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Building2, Search, AlertTriangle, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import "@/product-ux.css";

export default function Clients() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // { client, productCount }
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients");
      setItems(r.data);
    } catch { toast.error("Errore caricamento clienti"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const createNew = async () => {
    try {
      const r = await api.post("/clients", { name: "Nuovo cliente" });
      nav(`/clients/${r.data.id}`);
    } catch { toast.error("Errore creazione cliente"); }
  };
  const askDelete = async (c) => {
    setDeleteTarget({ client: c, productCount: null });
    try {
      const r = await api.get(`/clients/${c.id}/products`);
      const n = Array.isArray(r.data) ? r.data.length : (r.data?.items?.length ?? 0);
      setDeleteTarget({ client: c, productCount: n });
    } catch {
      setDeleteTarget({ client: c, productCount: 0 });
    }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${deleteTarget.client.id}?force=true`);
      toast.success("Cliente e prodotti associati eliminati");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    } finally { setDeleting(false); }
  };
  const filtered = items.filter(c => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [c.name, c.company_name, c.vat_number, c.email, c.city]
      .some(v => (v || "").toLowerCase().includes(s));
  });
  return (
    <div className="workflow-ux p-4 pt-16 md:p-8 lg:p-12" data-testid="clients-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Anagrafica</div>
          <h1 className="font-serif text-3xl md:text-4xl">Clienti</h1>
          <div className="text-sm text-[#707070] mt-1">
            Gestione clienti conto terzi (per l'export CPNP con intestazione personalizzata).
          </div>
        </div>
        {canWrite && <button onClick={createNew} className="btn-primary flex items-center" data-testid="client-new-btn">
          <Plus className="w-3 h-3 mr-2" strokeWidth={2} />Nuovo Cliente
        </button>}
      </div>
      <div className="mb-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#707070]" strokeWidth={1.5} />
        <input value={q} onChange={e=>setQ(e.target.value)}
                placeholder="Cerca per nome, ragione sociale, P.IVA, email, città..."
                className="input-luxury text-sm w-full !pl-10"
                data-testid="clients-search" />
      </div>
      {loading ? (
        <div className="text-sm text-[#707070]">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5E5E0] rounded-sm" data-testid="clients-empty">
          <Building2 className="w-8 h-8 mx-auto text-[#C5B39B] mb-3" strokeWidth={1.5} />
          <div className="text-sm text-[#707070]">{q ? "Nessun cliente corrisponde alla ricerca." : "Nessun cliente creato. Inizia da 'Nuovo Cliente'."}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-list">
          {filtered.map(c => (
            <div key={c.id} className="border border-[#E5E5E0] bg-white p-5 hover:shadow-sm hover:border-[#C5B39B] transition cursor-pointer group"
                 onClick={()=>nav(`/clients/${c.id}`)}
                 data-testid={`client-card-${c.id}`}>
              <div className="flex items-start gap-3 mb-3">
                {c.logo_file_id ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.logo_file_id}`}
                       className="w-12 h-12 object-contain bg-[#FAFAF7] p-1" alt={c.name}/>
                ) : (
                  <div className="w-12 h-12 bg-[#F4F4F0] flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#C5B39B]" strokeWidth={1.5}/>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#1A1A1A] truncate">{c.name}</div>
                  <div className="text-xs text-[#707070] truncate">{c.company_name || "—"}</div>
                </div>
              </div>
              <div className="text-xs text-[#707070] space-y-1">
                {c.vat_number && <div>P.IVA: {c.vat_number}</div>}
                {(c.city || c.cap) && <div>{[c.cap, c.city].filter(Boolean).join(" ")}</div>}
                {c.email && <div className="truncate">{c.email}</div>}
              </div>
              {canWrite && <div className="mt-4 pt-3 border-t border-[#F0F0EA] flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition">
                <button onClick={(e)=>{ e.stopPropagation(); nav(`/clients/${c.id}`); }}
                        className="text-[#707070] hover:text-[#1A1A1A]" title="Modifica"
                        data-testid={`client-edit-${c.id}`}>
                  <Edit2 className="w-3 h-3"/>
                </button>
                <button onClick={(e)=>{ e.stopPropagation(); askDelete(c); }}
                        className="text-[#8C3A3A] hover:opacity-70" title="Elimina"
                        data-testid={`client-delete-${c.id}`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>}
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={()=>!deleting && setDeleteTarget(null)} data-testid="client-delete-modal">
          <div className="bg-white w-full max-w-md border border-[#E5E5E0] shadow-xl" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between px-6 pt-5 pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F7E9E4] flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-[#8C3A3A]" strokeWidth={1.5}/>
                </div>
                <h2 className="font-serif text-xl leading-tight">Eliminare il cliente?</h2>
              </div>
              <button onClick={()=>setDeleteTarget(null)} disabled={deleting}
                      className="text-[#707070] hover:text-[#1A1A1A] disabled:opacity-40" data-testid="client-delete-modal-close">
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="px-6 pb-5 text-sm text-[#4A4A4A] space-y-3">
              <p>
                Stai per eliminare <strong>{deleteTarget.client.company_name || deleteTarget.client.name}</strong>.
              </p>
              <div className="border border-[#E9D6CF] bg-[#FBF3F0] rounded-sm px-4 py-3 text-[#8C3A3A]">
                <strong>Attenzione:</strong> eliminando il cliente verranno persi{" "}
                <strong>
                  {deleteTarget.productCount === null
                    ? "tutti i prodotti associati"
                    : `${deleteTarget.productCount} prodott${deleteTarget.productCount === 1 ? "o" : "i"} associat${deleteTarget.productCount === 1 ? "o" : "i"}`}
                </strong>. L'operazione è irreversibile.
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#F0F0EA]">
              <button onClick={()=>setDeleteTarget(null)} disabled={deleting}
                      className="btn-ghost text-sm disabled:opacity-40" data-testid="client-delete-cancel">Annulla</button>
              <button onClick={confirmDelete} disabled={deleting || deleteTarget.productCount === null}
                      className="text-sm px-4 py-2 bg-[#8C3A3A] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition rounded-sm inline-flex items-center gap-2"
                      data-testid="client-delete-confirm">
                <Trash2 className="w-3.5 h-3.5"/>
                {deleting ? "Eliminazione..." : "Elimina cliente e prodotti"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
